import type { BrainGuidelines, Tool } from '@/shared/types/brain';

/** Guideline field names that contain markdown text with potential tool references */
const GUIDELINE_TEXT_FIELDS = [
  'goal',
  'overview',
  'features',
  'loyalty',
  'custom_instructions',
  'live_instructions',
] as const;

/**
 * Escapes special regex characters in a string so it can be used in a RegExp constructor.
 */
const escapeRegex = (str: string): string => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Checks whether a given identifier (node_id or legacy dialog name) is referenced
 * as `@trigger_workflow(identifier)` in any of the brain's guideline fields.
 *
 * Scans: goal, overview, features, loyalty, custom_instructions, live_instructions,
 * objections (when string), and custom_guidelines[].body.
 */
export const isDialogReferencedInGuidelines = (
  guidelines: BrainGuidelines | undefined,
  identifier: string
): boolean => {
  if (!guidelines || !identifier) {
    return false;
  }

  const escaped = escapeRegex(identifier);
  const pattern = new RegExp(`@trigger_workflow\\((?:node_id="${escaped}"|${escaped})\\)`);

  // Check standard text fields
  for (const field of GUIDELINE_TEXT_FIELDS) {
    const value = guidelines[field];
    if (typeof value === 'string' && pattern.test(value)) {
      return true;
    }
  }

  // Check objections when stored as string
  if (typeof guidelines.objections === 'string' && pattern.test(guidelines.objections)) {
    return true;
  }

  // Check custom guidelines bodies
  if (guidelines.custom_guidelines) {
    for (const cg of guidelines.custom_guidelines) {
      if (pattern.test(cg.body)) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Returns a copy of the guidelines with all `@trigger_workflow(identifier)` references
 * stripped from every text field. Only returns fields that were actually modified.
 */
export const removeToolReferencesFromGuidelines = (
  guidelines: BrainGuidelines | undefined,
  identifier: string
): Partial<BrainGuidelines> => {
  if (!guidelines || !identifier) {
    return {};
  }

  const escaped = escapeRegex(identifier);
  const pattern = new RegExp(`@trigger_workflow\\((?:node_id="${escaped}"|${escaped})\\)`, 'g');
  const patch: Partial<BrainGuidelines> = {};

  // Clean standard text fields
  for (const field of GUIDELINE_TEXT_FIELDS) {
    const value = guidelines[field];
    if (typeof value === 'string') {
      const cleaned = value.replace(pattern, '');
      if (cleaned !== value) {
        (patch as Record<string, string>)[field] = cleaned;
      }
    }
  }

  // Clean objections when stored as string
  if (typeof guidelines.objections === 'string') {
    const cleaned = guidelines.objections.replace(pattern, '');
    if (cleaned !== guidelines.objections) {
      patch.objections = cleaned;
    }
  }

  // Clean custom guidelines bodies
  if (guidelines.custom_guidelines) {
    const cleaned = guidelines.custom_guidelines.map((cg) => {
      const body = cg.body.replace(pattern, '');
      return body !== cg.body ? { ...cg, body } : cg;
    });
    const anyChanged = cleaned.some((cg, i) => cg !== guidelines.custom_guidelines![i]);
    if (anyChanged) {
      patch.custom_guidelines = cleaned;
    }
  }

  return patch;
};

/**
 * Builds a regex that matches all `@toolName(...)` or bare `@toolName` references
 * for a given tool in guideline text.
 */
const buildToolPattern = (tool: Tool, flags = ''): RegExp =>
  new RegExp(`@${escapeRegex(tool)}\\b(\\([^)]*\\))?`, flags);

/**
 * Checks whether a given tool is referenced as `@toolName` or `@toolName(...)`
 * in any of the brain's guideline fields.
 */
export const isToolReferencedInGuidelines = (
  guidelines: BrainGuidelines | undefined,
  tool: Tool
): boolean => {
  if (!guidelines) {
    return false;
  }

  const pattern = buildToolPattern(tool);

  for (const field of GUIDELINE_TEXT_FIELDS) {
    const value = guidelines[field];
    if (typeof value === 'string' && pattern.test(value)) {
      return true;
    }
  }

  if (typeof guidelines.objections === 'string' && pattern.test(guidelines.objections)) {
    return true;
  }

  if (guidelines.custom_guidelines) {
    for (const cg of guidelines.custom_guidelines) {
      if (pattern.test(cg.body)) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Returns a copy of the guidelines with all `@toolName(...)` / `@toolName` references
 * stripped for a given tool. Only returns fields that were actually modified.
 */
export const removeAllReferencesOfToolFromGuidelines = (
  guidelines: BrainGuidelines | undefined,
  tool: Tool
): Partial<BrainGuidelines> => {
  if (!guidelines) {
    return {};
  }

  const pattern = buildToolPattern(tool, 'g');
  const patch: Partial<BrainGuidelines> = {};

  for (const field of GUIDELINE_TEXT_FIELDS) {
    const value = guidelines[field];
    if (typeof value === 'string') {
      const cleaned = value.replace(pattern, '');
      if (cleaned !== value) {
        (patch as Record<string, string>)[field] = cleaned;
      }
    }
  }

  if (typeof guidelines.objections === 'string') {
    const cleaned = guidelines.objections.replace(pattern, '');
    if (cleaned !== guidelines.objections) {
      patch.objections = cleaned;
    }
  }

  if (guidelines.custom_guidelines) {
    const cleaned = guidelines.custom_guidelines.map((cg) => {
      const body = cg.body.replace(pattern, '');
      return body !== cg.body ? { ...cg, body } : cg;
    });
    const anyChanged = cleaned.some((cg, i) => cg !== guidelines.custom_guidelines![i]);
    if (anyChanged) {
      patch.custom_guidelines = cleaned;
    }
  }

  return patch;
};
